import client from 'core/axios';
import { handleJSONResponse } from 'core/axios/handleResponse';
import RequestMethod from 'core/axios/RequestMethod';

/**
 * Used to authenticate the user's credentials
 * @param payload - Payload object with username and password fields.
 */
export const login = ({ version = 'v1', payload }) =>
  client
    .request({
      url: `${version}/sl_users/login`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse);

/**
 * Used to recover/claim an account
 * @param payload - Payload object with username and password fields.
 */
export const recoverPassword = ({ version = 'v1', payload }) =>
  client
    .request({
      url: `${version}/sl_users/forgot`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse);

/**
 * Used to verify claim code when user loads the onboarding page
 * @param {string} entityType - Different types and mapping detailed at
 * modules/OnboardingAndLogin/constants
 * @param {string} claimCode - Unique hash id
 */
export const verifyClaimCode = ({
  version = 'v1',
  entityType,
  claimCode,
} = {}) =>
  client
    .request({
      url: `${version}/onboarding/member/verify/${entityType}/${claimCode}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse);

/**
 * Used to create the user account
 * @param payload.firstName {string}
 * @param payload.lastName {string}
 * @param payload.email {string}
 * @param payload.password {string}
 * @param payload.phoneNumber {string}
 * @param payload.picture {string}
 * @param payload.<entityType> {string} - refer to OnboardingAndLogin README for entity type information
 * @param payload.employer {number} - employer id selected from AffiliationsScreen
 * @param payload.heSchool {number} - he school id selected from AffiliationsScreen
 * @param payload.crmAffiliations {number[]} - array of crm affiliation ids selected from AffiliationsScreen
 */
export const createUser = ({ version = 'v1', payload } = {}) =>
  client
    .request({
      url: `${version}/onboarding/member/create-user/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse);

/**
 * Used to associate the newly created user account with the user's account type
 * A list of different entity types can be found in modules/OnboardingAndLogin/constants
 * The payload will be shaped differently for district and guardian account types
 * More info at modules/OnboardingAndLogin/components/AccountSetupScreen
 * @param {Object} payload
 */
export const associateUser = ({ version = 'v1', payload } = {}) =>
  client
    .request({
      url: `${version}/onboarding/member/associate/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse);

/**
 * Used by logged in guardians to fetch all claimed students for a guardian
 */
export const listClaimedStudent = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl_users/guardian-students/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse);

/**
 * Used by logged in guardians to delete a claimed student
 * @param {string} studentClaimCode - Student's claim code
 */
export const deleteClaimedStudent = ({
  version = 'v1',
  studentClaimCode,
} = {}) =>
  client
    .request({
      url: `${version}/sl_users/guardian-students/${studentClaimCode}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse);

/**
 * Used by logged in guardians to update a relation for a claimed student
 * @param {string} studentClaimCode - Student's claim code
 * @param {Object} payload - { relation }
 */
export const updateClaimedStudent = ({
  version = 'v1',
  studentClaimCode,
  payload,
} = {}) =>
  client
    .request({
      url: `${version}/sl_users/guardian-students/${studentClaimCode}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse);

/**
 * Used by logged in guardians to claim a new student
 * @param {Object} payload - { claimCode, relation }
 */
export const createClaimedStudent = ({ version = 'v1', payload } = {}) =>
  client
    .request({
      url: `${version}/sl_users/guardian-students/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse);

/**
 * Used to convert district uuid to a claim code
 * @param {string} districtUUID - uuid param that is grabbed from old district
 * alum links
 */
export const retrieveDistrictInfo = ({ version = 'v1', districtUUID }) =>
  client
    .request({
      url: `${version}/sl-schools/entity/district/${districtUUID}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse);

/**
 * Used by student accounts transitioning to alum accounts. Checks if
 * the account needs to accept terms of service
 */
export const retrieveAlumInfo = ({ version = 'v1' }) =>
  client
    .request({
      url: `${version}/alumni/alum/user/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse);

/**
 * Used by student accounts transitioning to alum accounts by updating key
 */
export const updateAlumInfo = ({ version = 'v1', uuid, payload }) =>
  client
    .request({
      url: `${version}/alumni/alum/${uuid}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse);

/**
 * Used to fetch a unique invitation link for district industry partners and college reps
 * type can either be DISTRICT_INDUSTRY_PARTNER, DISTRICT_COLLEGE_REP, or CRM_RECORD
 */
export const getIPDBInvitationLink = ({ version = 'v1', type, hashId }) =>
  client
    .request({
      url: `${version}/onboarding/claim-link/generate/${type}/${hashId}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse);

/**
 * Used by AccountSetupScreen once the user pressed the continue button to
 * validate user information
 * Will throw 400 if existing email/phone number or if phone number is invalid
 * @param payload.firstName
 * @param payload.lastName
 * @param payload.email
 * @param payload.password
 * @param payload.phoneNumber
 * @param payload.picture
 *
 * Additional params with hash id
 * @param payload.crmRecord
 * @param payload.organization
 * @param payload.organizationInvite
 *
 * @returns data.crmAffiliations - {array}
 * @returns data.organization - {Object | null}
 */
export const validate = ({ version = 'v1', payload }) =>
  client
    .request({
      url: `${version}/onboarding/member/validate/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse);
